import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Space, 
  Card, 
  Select, 
  Switch, 
  Divider, 
  Typography, 
  message, 
  InputNumber, 
  Upload,
  Row,
  Col,
  Collapse
} from 'antd';
import { 
  PlusOutlined, 
  MinusCircleOutlined, 
  UploadOutlined,
  DeleteOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { Survey, Question, Option } from '../services/surveyService';
import type { FormListFieldData, FormListOperation } from 'antd/es/form/FormList';

const { TextArea } = Input;
const { Title } = Typography;
const { Option: SelectOption } = Select;
const { Panel } = Collapse;

interface SurveyEditorProps {
  initialData?: Survey;
  onSave: (survey: Survey) => void;
  onCancel: () => void;
}

// 问题类型选项
const QUESTION_TYPES = [
  { value: 'text', label: '填空题', icon: '📝' },
  { value: 'textarea', label: '文本题', icon: '📄' },
  { value: 'radio', label: '单选题', icon: '🔘' },
  { value: 'checkbox', label: '多选题', icon: '☑️' },
  { value: 'file', label: '文件上传', icon: '📎' },
  { value: 'sort', label: '排序题', icon: '🔀' }
];

// 问卷类型选项
const SURVEY_TYPES = [
  { value: 'research', label: '调研问卷' },
  { value: 'assistance', label: '援助问卷' }
];

const SurveyEditor: React.FC<SurveyEditorProps> = ({ initialData, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
    if (initialData) {
      // 将问题按分组整理
      const groupedQuestions = groupQuestionsBySection(initialData.questions || []);
      
      form.setFieldsValue({
        title: initialData.title,
        description: initialData.description,
        type: initialData.type,
        price: initialData.price,
        sections: groupedQuestions
      });
    }
  }, [initialData, form]);

  // 按分组整理问题
  const groupQuestionsBySection = (questions: Question[]) => {
    const sectionMap: { [key: string]: Question[] } = {};
    
    questions.forEach(question => {
      const section = question.section || '默认分组';
      if (!sectionMap[section]) {
        sectionMap[section] = [];
      }
      sectionMap[section].push(question);
    });

    return Object.entries(sectionMap).map(([sectionName, questions]) => ({
      name: sectionName,
      questions: questions.sort((a, b) => (a.order_num || 0) - (b.order_num || 0))
    }));
  };

  // 渲染问题选项
  const renderQuestionOptions = (questionIndex: number, sectionIndex: number, questionType: string) => {
    if (!['radio', 'checkbox', 'sort'].includes(questionType)) {
      return null;
    }

    return (
      <Form.List name={[questionIndex, 'options']}>
        {(fields, { add, remove }) => (
          <div style={{ marginLeft: 20 }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>选项设置：</div>
            {fields.map(({ key, name, ...restField }) => (
              <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                <Col span={10}>
                  <Form.Item
                    {...restField}
                    name={[name, 'label']}
                    rules={[{ required: true, message: '请输入选项文本' }]}
                  >
                    <Input placeholder="选项文本" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'value']}
                    rules={[{ required: true, message: '请输入选项值' }]}
                  >
                    <Input placeholder="选项值" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    {...restField}
                    name={[name, 'order_num']}
                  >
                    <InputNumber placeholder="序号" min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => remove(name)}
                  />
                </Col>
                {questionType !== 'sort' && <Col span={24}>
                  <Space wrap align="start">
                    <Form.Item name={[name, 'has_input']} label="选中后补充填写" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name={[name, 'input_label']} label="补充填写提示"><Input placeholder="请注明具体内容" /></Form.Item>
                    <Form.Item name={[name, 'input_required']} label="选中后补充必填" valuePropName="checked"><Switch /></Form.Item>
                    {questionType === 'checkbox' && <Form.Item name={[name, 'exclusive']} label="与其他选项互斥" valuePropName="checked"><Switch /></Form.Item>}
                  </Space>
                </Col>}
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ label: '', value: '', order_num: fields.length + 1 })}
              block
              icon={<PlusOutlined />}
            >
              添加选项
            </Button>
          </div>
        )}
      </Form.List>
    );
  };

  // 渲染文件上传配置
  const renderFileUploadConfig = (questionIndex: number, sectionIndex: number) => {
    return (
      <div style={{ marginLeft: 20 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={[questionIndex, 'file_types']}
              label="允许的文件类型"
            >
              <Select
                mode="multiple"
                placeholder="选择允许的文件类型"
                options={[
                  { value: 'image/*', label: '图片文件' },
                  { value: '.pdf', label: 'PDF文件' },
                  { value: '.doc,.docx', label: 'Word文档' },
                  { value: '.xls,.xlsx', label: 'Excel文档' },
                  { value: '.txt', label: '文本文件' }
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={[questionIndex, 'max_size']}
              label="最大文件大小(MB)"
            >
              <InputNumber min={1} max={100} placeholder="请输入最大文件大小" />
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  // 渲染单个问题
  const renderQuestion = (questionField: FormListFieldData, questionIndex: number, sectionIndex: number, questionOperations: FormListOperation) => {
    return (
      <Card
        key={questionField.key}
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Form.Item
            name={[questionIndex, 'title']}
            rules={[{ required: true, message: '请输入问题标题' }]}
            style={{ margin: 0 }}
          >
            <Input placeholder="请输入问题标题" />
          </Form.Item>
        }
        extra={
          <Space>
            <Button 
              type="text" 
              icon={<CopyOutlined />} 
              onClick={() => {
                const values = form.getFieldValue(['sections', sectionIndex, 'questions', questionIndex]);
                questionOperations.add({ ...values, id: undefined, title: values.title + ' - 副本' });
              }}
            />
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => questionOperations.remove(questionIndex)}
            />
          </Space>
        }
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={[questionIndex, 'type']}
              label="问题类型"
              rules={[{ required: true, message: '请选择问题类型' }]}
            >
              <Select placeholder="选择问题类型">
                {QUESTION_TYPES.map(type => (
                  <SelectOption key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </SelectOption>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={[questionIndex, 'answered_by']}
              label="回答人"
              rules={[{ required: true, message: '请选择回答人' }]}
            >
              <Select placeholder="选择回答人">
                <SelectOption value="user">用户回答</SelectOption>
                <SelectOption value="approver">审批人回答</SelectOption>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name={[questionIndex, 'required']}
              label="是否必填"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name={[questionIndex, 'order_num']}
              label="排序"
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {/* 根据问题类型渲染不同的配置 */}
        <Form.Item shouldUpdate={(prevValues, currentValues) => {
          const prevType = prevValues.sections?.[sectionIndex]?.questions?.[questionIndex]?.type;
          const currentType = currentValues.sections?.[sectionIndex]?.questions?.[questionIndex]?.type;
          return prevType !== currentType;
        }}>
          {({ getFieldValue }) => {
            const questionType = getFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'type']);
            
            if (['radio', 'checkbox', 'sort'].includes(questionType)) {
              return <>
                {renderQuestionOptions(questionIndex, sectionIndex, questionType)}
                {questionType === 'radio' && <Row gutter={16}>
                  <Col span={16}><Form.Item name={[questionIndex, 'supplement_label']} label="题后说明（填写提示后启用）"><Input placeholder="例如：请简要说明理由" /></Form.Item></Col>
                  <Col span={8}><Form.Item name={[questionIndex, 'supplement_required']} label="题后说明必填" valuePropName="checked"><Switch /></Form.Item></Col>
                </Row>}
              </>;
            }
            
            if (questionType === 'file') {
              return renderFileUploadConfig(questionIndex, sectionIndex);
            }
            
            return null;
          }}
        </Form.Item>
      </Card>
    );
  };

  // 渲染问题分组
  const renderSection = (sectionField: FormListFieldData, sectionIndex: number, sectionOperations: FormListOperation) => {
    return (
      <Panel
        key={sectionField.key}
        header={
          <Form.Item
            name={[sectionIndex, 'name']}
            rules={[{ required: true, message: '请输入分组名称' }]}
            style={{ margin: 0 }}
          >
            <Input placeholder="分组名称" />
          </Form.Item>
        }
        extra={
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              sectionOperations.remove(sectionIndex);
            }}
          />
        }
      >
        <Form.List name={[sectionIndex, 'questions']}>
          {(questionFields, questionOperations) => (
            <>
              {questionFields.map((questionField, questionIndex) => 
                renderQuestion(questionField, questionIndex, sectionIndex, questionOperations)
              )}
              <Button
                type="dashed"
                onClick={() => questionOperations.add({
                  title: '',
                  type: 'text',
                  answered_by: 'user',
                  required: false,
                  order_num: questionFields.length + 1,
                  options: [],
                  allow_other: false,
                  level: 0,
                  parent_id: null
                })}
                block
                icon={<PlusOutlined />}
              >
                添加问题
              </Button>
            </>
          )}
        </Form.List>
      </Panel>
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue(true);
      
      // 将分组结构转换为平铺结构
      const flattenedQuestions: Question[] = [];
      let globalOrder = 1;
      
      (values.sections || []).forEach((section: any) => {
        (section.questions || []).forEach((question: any) => {
          flattenedQuestions.push({
            ...question,
            section: section.name,
            order_num: globalOrder++,
            options: question.options || []
          });
        });
      });

      const surveyData: Survey = {
        id: initialData?.id,
        title: values.title,
        description: values.description,
        type: values.type,
        price: values.price,
        questions: flattenedQuestions
      };

      onSave(surveyData);
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请完善表单信息');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'research'
        }}
      >
        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="问卷标题"
                rules={[{ required: true, message: '请输入问卷标题' }]}
              >
                <Input placeholder="请输入问卷标题" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="问卷类型"
                rules={[{ required: true, message: '请选择问卷类型' }]}
              >
                <Select placeholder="选择问卷类型">
                  {SURVEY_TYPES.map(type => (
                    <SelectOption key={type.value} value={type.value}>
                      {type.label}
                    </SelectOption>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="问卷描述"
            rules={[{ required: true, message: '请输入问卷描述' }]}
          >
            <TextArea rows={3} placeholder="请输入问卷描述" />
          </Form.Item>

          <Form.Item
            name="price"
            label="劳务费(元)"
          >
            <InputNumber min={0} step={0.01} style={{ width: '50%' }} placeholder="请输入劳务费" />
          </Form.Item>
        </Card>

        {/* 问题设置 */}
        <Card title="问题设置" style={{ marginBottom: 16 }}>
          <Form.List name="sections">
            {(sectionFields, sectionOperations) => (
              <>
                <Collapse>
                  {sectionFields.map((sectionField, sectionIndex) => 
                    renderSection(sectionField, sectionIndex, sectionOperations)
                  )}
                </Collapse>
                
                <Button
                  type="dashed"
                  onClick={() => sectionOperations.add({
                    name: `分组${sectionFields.length + 1}`,
                    questions: []
                  })}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 16 }}
                >
                  添加分组
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {/* 操作按钮 */}
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              取消
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              保存
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default SurveyEditor; 